import { Fragment, useState ,memo} from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card } from './BmsCardPrimitives'
import ChartPlaceholder from '../../components/ChartPlaceholder'
import LoadingOverlay from '../../components/LoadingOverlay'
import { formatChartTimestamp, computeAxisTicks } from '../../socket/useTelemetryHistory'
import ChartTooltip from './ChartTooltip'

// Used for the Latest/Min/Max legend cells below — ChartTooltip.jsx has its
// own copy for the hover tooltip itself.
const formatValue = (value, unit) => (value != null ? `${value}${unit}` : '-')

const LABEL_COL_WIDTH = 96
const VALUE_COL_WIDTH = 80

// domain: optional [fromMs, toMs] — when given, the x-axis always spans the
// full requested range (e.g. a whole "Last 1 Day") instead of cropping to
// wherever real data happens to start/end. Omitted for the live "Realtime"
// window, which auto-scales to the rolling buffer as before.
// tickCount: target number of x-axis ticks across that domain (default 8).
// isLoading/error: the state of the fetch feeding `chart`. A wide history range
// can take seconds to arrive, and an empty grid with a blank legend is
// indistinguishable from a range that genuinely has no data — so the plot area
// is replaced by ChartPlaceholder until there's something real to draw.
const TelemetryChart = memo(({ title, chart, height = 260, domain, tickCount = 8, isLoading = false, error = null, onRetry, emptyMessage }) => {
  const [hiddenSeries, setHiddenSeries] = useState([])
  const rangeMs = domain ? domain[1] - domain[0] : undefined
  const formatTick = (epochMs) => formatChartTimestamp(epochMs, rangeMs)
  const axisTicks = domain ? computeAxisTicks(domain[0], domain[1], tickCount) : undefined
  // Every series in one chart instance shares the same unit (all Cell N are
  // mV, all NTC N are °C) — the first legend entry's unit speaks for all.
  const yUnit = chart.legend[0]?.unit ?? ''

  // Same rule CustomTable uses: only a load with nothing on screen yet swaps
  // the plot out. A refetch that already has points keeps drawing them (dimmed)
  // rather than flashing back to a spinner — realtime ticks refetch constantly.
  const isEmpty = chart.points.length === 0 || chart.legend.length === 0
  const showError = !isLoading && Boolean(error)

  const toggleSeries = (name) => {
    setHiddenSeries((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]))
  }

  const toggleAll = () => {
    setHiddenSeries((prev) => (prev.length > 0 ? [] : chart.legend.map((s) => s.name)))
  }

  return (
    <Card className="gap-3">
      <span className="text-sm font-medium text-[#252a34] flex items-center gap-1.5">
        <span className="material-icons text-base text-[#616a79]">show_chart</span>
        {title}
      </span>
      {isEmpty ? (
        <ChartPlaceholder
          height={height}
          isLoading={isLoading}
          error={showError ? error : null}
          onRetry={onRetry}
          emptyMessage={emptyMessage}
        />
      ) : (
      <div className="relative">
      {isLoading && <LoadingOverlay label="Loading telemetry…" />}
      <div className={`flex flex-col gap-3 transition-opacity ${isLoading ? 'opacity-50' : ''}`} aria-busy={isLoading}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chart.points} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="#e8ecf3" vertical={false} />
          <XAxis
            dataKey="epochMs"
            type="number"
            domain={domain ?? ['auto', 'auto']}
            scale="time"
            ticks={axisTicks}
            interval={axisTicks ? 0 : 'preserveStartEnd'}
            tickFormatter={formatTick}
            tick={{ fontSize: 11, fill: '#898781' }}
            axisLine={{ stroke: '#c3c2b7' }}
            tickLine={false}
          />
          <YAxis tickFormatter={(v) => `${v}${yUnit}`} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} />
          {/* wrapperStyle z-index: a tall tooltip (24 cells) overflows the card
              and lands on top of the next card down, which is `relative` and
              later in the DOM — without a z-index the tooltip is painted under
              it. No ancestor here sets a z-index, so 30 competes at the root
              and still stays under the sticky app header (z-999). */}
          <Tooltip content={<ChartTooltip legend={chart.legend} hiddenSeries={hiddenSeries} />} cursor={{ stroke: '#c3c2b7', strokeWidth: 1 }} isAnimationActive={false} offset={28} wrapperStyle={{ zIndex: 30 }} />
          {chart.legend.map((s) => {
            const hidden = hiddenSeries.includes(s.name)
            return (
              /* Every series stays mounted (hidden ones just go transparent) so
                 toggling off/back on can't change Recharts' internal tooltip
                 order — Recharts orders the tooltip payload by <Line> mount
                 order, and unmounting+remounting a filtered-out Line bumped it
                 to the end instead of its natural position.
                 Animation off: with live data streaming in, every update kicks
                 off a full-path animation that piles up faster than it finishes
                 and retains detached SVG state between renders. */
              <Line
                key={s.name}
                type="monotone"
                dataKey={s.name}
                stroke={hidden ? 'transparent' : s.color}
                strokeWidth={2}
                dot={false}
                activeDot={hidden ? false : { r: 4 }}
                isAnimationActive={false}
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend stats stay with the plot: with no points every Latest/Min/Max
          would render as a bare unit ("V"), which reads as a real reading. */}
      <div className="flex flex-col gap-2 border-t border-[#dfe4ed] pt-3">
        <div className="flex gap-4">
          <div className="flex flex-col gap-1 shrink-0" style={{ width: LABEL_COL_WIDTH }}>
            <button
              onClick={toggleAll}
              className="px-2 py-0.5 rounded border border-[#dfe4ed] bg-[#f2f4f7] text-xs font-normal text-[#616a79] whitespace-nowrap"
            >
              All Series
            </button>
            <span className="text-xs font-medium text-[#616a79] h-5 flex items-center">Latest</span>
            <span className="text-xs font-medium text-[#616a79] h-5 flex items-center">Min</span>
            <span className="text-xs font-medium text-[#616a79] h-5 flex items-center">Max</span>
          </div>
          {/* Single scroll container for the chip row + all 3 value rows —
              they used to be two containers kept in sync via a JS scroll
              handler, which only synced one-way (value grid -> chips), so
              scrolling the chip row directly desynced it from the values.
              One shared scroll container makes that impossible. */}
          <div
            className="overflow-x-auto flex-1 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#c8cfdc] [&::-webkit-scrollbar-thumb]:rounded-full"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#c8cfdc transparent' }}
          >
            <div
              className="grid gap-x-4 gap-y-1"
              style={{ gridTemplateColumns: `repeat(${chart.legend.length}, ${VALUE_COL_WIDTH}px)`, gridTemplateRows: 'auto repeat(3, 20px)' }}
            >
              {chart.legend.map((s, i) => {
                const hidden = hiddenSeries.includes(s.name)
                const cellClass = `text-xs font-medium whitespace-nowrap flex items-center ${hidden ? 'text-[#98a2b3]' : 'text-[#272d37]'}`
                return (
                  <Fragment key={s.name}>
                    <button
                      onClick={() => toggleSeries(s.name)}
                      style={{ gridColumn: i + 1, gridRow: 1 }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#dfe4ed] bg-[#f9fafa] text-xs font-normal whitespace-nowrap"
                    >
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: hidden ? '#c8cfdc' : s.color }} />
                      <span className={hidden ? 'text-[#98a2b3]' : 'text-[#616a79]'}>{s.name}</span>
                    </button>
                    <span style={{ gridColumn: i + 1, gridRow: 2 }} className={cellClass}>{formatValue(s.latest, s.unit ?? ' V')}</span>
                    <span style={{ gridColumn: i + 1, gridRow: 3 }} className={cellClass}>{formatValue(s.min, s.unit ?? ' V')}</span>
                    <span style={{ gridColumn: i + 1, gridRow: 4 }} className={cellClass}>{formatValue(s.max, s.unit ?? ' V')}</span>
                  </Fragment>
                )
              })}
            </div>
          </div>
        </div>
      </div>
      </div>
      </div>
      )}
    </Card>
  )
})

export default TelemetryChart